'use client';
import React, { useState, useEffect } from 'react';
import { Tv2, Radio, Calendar, Users, Wifi, Globe, TrendingUp } from 'lucide-react';
import { streamService } from '@/lib/services/streamService';
import { useAuth } from '@/contexts/AuthContext';

function formatViewers(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

export default function DashboardMetricsBar() {
  const { isAdmin, isModerator } = useAuth();
  const [metrics, setMetrics] = useState<{
    activeStreams: number;
    totalViewers: number;
    streamHealth: string;
    countries: number;
    degradedCount: number;
    activeTVCount: number;
    activeRadioCount: number;
    activeEventsCount: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      streamService.getDashboardMetrics(),
      streamService.getTVChannels(),
      streamService.getRadioStations(),
      streamService.getLiveEvents(),
    ]).then(([dashMetrics, tvChannels, radioStations, liveEvents]) => {
      const activeTVCount = tvChannels.filter((c) => c.streamStatus === 'live').length;
      const activeRadioCount = radioStations.filter((r) => r.streamStatus === 'live').length;
      const activeEventsCount = liveEvents.filter((e) => e.eventStatus === 'live').length;
      setMetrics({ ...dashMetrics, activeTVCount, activeRadioCount, activeEventsCount });
      setLoading(false);
    });
  }, []);

  // Non-admin: show only TV, Radio, Events counts
  if (!isAdmin && !isModerator) {
    const simpleItems = [
      {
        id: 'metric-tv',
        label: 'Active TV Channels',
        value: loading ? '—' : String(metrics?.activeTVCount ?? 0),
        sub: 'Live right now',
        icon: <Tv2 size={16} />,
        color: 'text-red-500',
        bg: 'bg-[rgba(220,38,38,0.08)]',
        border: 'border-[rgba(220,38,38,0.15)]',
      },
      {
        id: 'metric-radio',
        label: 'Active Radio Stations',
        value: loading ? '—' : String(metrics?.activeRadioCount ?? 0),
        sub: 'Broadcasting now',
        icon: <Radio size={16} />,
        color: 'text-white',
        bg: 'bg-[rgba(255,255,255,0.05)]',
        border: 'border-[rgba(255,255,255,0.1)]',
      },
      {
        id: 'metric-events',
        label: 'Active Live Events',
        value: loading ? '—' : String(metrics?.activeEventsCount ?? 0),
        sub: 'Happening now',
        icon: <Calendar size={16} />,
        color: 'text-red-400',
        bg: 'bg-[rgba(220,38,38,0.06)]',
        border: 'border-[rgba(220,38,38,0.12)]',
      },
    ];

    return (
      <div className="grid grid-cols-3 gap-3">
        {simpleItems.map((m) => (
          <div
            key={m.id}
            className={`${m.bg} border ${m.border} rounded-xl p-3.5 flex items-start gap-3`}
          >
            <div className={`${m.color} mt-0.5 flex-shrink-0`}>{m.icon}</div>
            <div className="min-w-0">
              <p className="text-[11px] font-500 text-[rgb(115,115,115)] tracking-wide uppercase truncate">{m.label}</p>
              <p className={`text-xl font-700 tabular-nums ${m.color} leading-tight mt-0.5`}>{m.value}</p>
              <p className="text-[11px] text-[rgb(115,115,115)] mt-0.5 truncate">{m.sub}</p>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Admin/Moderator: show full metrics
  const metricItems = [
    {
      id: 'metric-active-streams',
      label: 'Active Streams',
      value: loading ? '—' : String(metrics?.activeStreams ?? 0),
      sub: loading ? 'Loading...' : `${metrics?.degradedCount ?? 0} degraded`,
      icon: <Tv2 size={16} />,
      color: 'text-red-500',
      bg: 'bg-[rgba(220,38,38,0.08)]',
      border: 'border-[rgba(220,38,38,0.15)]',
    },
    {
      id: 'metric-viewers',
      label: 'Concurrent Viewers',
      value: loading ? '—' : formatViewers(metrics?.totalViewers ?? 0),
      sub: loading ? 'Loading...' : 'Live right now',
      icon: <Users size={16} />,
      color: 'text-white',
      bg: 'bg-[rgba(255,255,255,0.05)]',
      border: 'border-[rgba(255,255,255,0.1)]',
    },
    {
      id: 'metric-health',
      label: 'Stream Health',
      value: loading ? '—' : `${metrics?.streamHealth ?? 0}%`,
      sub: loading ? 'Loading...' : `${metrics?.degradedCount ?? 0} streams degraded`,
      icon: <Wifi size={16} />,
      color: 'text-amber-400',
      bg: 'bg-[rgba(245,158,11,0.08)]',
      border: 'border-[rgba(245,158,11,0.15)]',
    },
    {
      id: 'metric-countries',
      label: 'Countries Covered',
      value: loading ? '—' : String(metrics?.countries ?? 0),
      sub: 'Across Africa',
      icon: <Globe size={16} />,
      color: 'text-sky-400',
      bg: 'bg-[rgba(56,189,248,0.08)]',
      border: 'border-[rgba(56,189,248,0.15)]',
    },
    {
      id: 'metric-new',
      label: 'Total Streams',
      value: loading ? '—' : String((metrics?.activeStreams ?? 0) + (metrics?.degradedCount ?? 0)),
      sub: 'TV, Radio & Events',
      icon: <TrendingUp size={16} />,
      color: 'text-emerald-400',
      bg: 'bg-[rgba(34,197,94,0.08)]',
      border: 'border-[rgba(34,197,94,0.15)]',
    },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      {metricItems?.map((m) => (
        <div
          key={m?.id}
          className={`${m?.bg} border ${m?.border} rounded-xl p-3.5 flex items-start gap-3`}
        >
          <div className={`${m?.color} mt-0.5 flex-shrink-0`}>{m?.icon}</div>
          <div className="min-w-0">
            <p className="text-[11px] font-500 text-[rgb(115,115,115)] tracking-wide uppercase truncate">{m?.label}</p>
            <p className={`text-xl font-700 tabular-nums ${m?.color} leading-tight mt-0.5`}>{m?.value}</p>
            <p className="text-[11px] text-[rgb(115,115,115)] mt-0.5 truncate">{m?.sub}</p>
          </div>
        </div>
      ))}
    </div>
  );
}